'use client';

import type { CrosstabItem } from '@/lib/crosstab/library/types';
import type { CrosstabDataSummary } from './crosstab-picker';
import { Button, Chip, Popover, PopoverContent, PopoverTrigger } from '@heroui/react';
import { Icon } from '@iconify/react';
import { motion } from 'framer-motion';
import { useCallback, useState } from 'react';
import { CrosstabPicker } from './crosstab-picker';
import { DataInputField } from './data-input-field';
import { ModuleCard } from './module-card';
import type { PlannerCardProps } from './props';
import { ReportSubmit } from './report-submit';
import { SectionWrapper } from './section-wrapper';
import type { PlanCardStepOption, PlannerField, PlannerModule, PlannerStep } from './types';

export function PlannerCard({
    plan,
    onSubmit,
    isReadOnly = false,
    loadingPathId = null,
    initialFormState,
    onOptionSelect,
    onFieldSubmit,
    onModuleSubmit,
    onCrosstabConfirm,
    selectedOptionId = null,
    isFieldSubmitted = false,
    isModuleSubmitted = false,
}: PlannerCardProps & {
    onCrosstabConfirm?: (crosstab: CrosstabItem, summary: CrosstabDataSummary) => void;
}) {
    const isStepMode = !!plan.stepMode;
    const isInlineWidget = plan.type === 'inline_widget';

    const [fieldValues, setFieldValues] = useState<Record<string, string | string[]>>(() => {
        const initial: Record<string, string | string[]> = {};
        for (const step of plan.steps) {
            if (!step.fields) continue;
            for (const field of step.fields) {
                const saved = initialFormState?.[field.key];
                if (saved !== undefined && saved !== null) {
                    initial[field.key] = saved as string | string[];
                } else if (field.type === 'multi_tag') {
                    initial[field.key] = field.values ?? [];
                } else {
                    initial[field.key] = field.value ?? '';
                }
            }
        }
        return initial;
    });

    const [modules, setModules] = useState<PlannerModule[]>(() => {
        const allModules: PlannerModule[] = [];
        for (const step of plan.steps) {
            if (step.modules) allModules.push(...step.modules);
            if (step.suggested_modules) {
                allModules.push(...step.suggested_modules.map((m) => ({ ...m, enabled: false })));
            }
        }
        const savedModuleIds = initialFormState?.enabled_modules;
        if (Array.isArray(savedModuleIds)) {
            const enabledSet = new Set(savedModuleIds);
            return allModules.map((m) => ({
                ...m,
                enabled: enabledSet.has(m.id),
            }));
        }
        return allModules;
    });

    const [selectedCrosstab, setSelectedCrosstab] = useState<CrosstabItem | null>(() => {
        const id = initialFormState?.crosstab_id;
        const name = initialFormState?.crosstab_name as string | undefined;
        if (id && name) {
            return { id: String(id), name } as CrosstabItem;
        }
        return null;
    });
    const [addTagField, setAddTagField] = useState<string | null>(null);
    const [newTagValue, setNewTagValue] = useState('');

    const handleRemoveTag = useCallback((key: string, value: string) => {
        setFieldValues((prev) => {
            const current = prev[key];
            if (Array.isArray(current)) {
                return { ...prev, [key]: current.filter((v) => v !== value) };
            }
            return prev;
        });
    }, []);

    const handleAddTag = useCallback((key: string, value: string) => {
        if (!value.trim()) return;
        setFieldValues((prev) => {
            const current = prev[key];
            if (Array.isArray(current)) {
                if (current.includes(value.trim())) return prev;
                return { ...prev, [key]: [...current, value.trim()] };
            }
            return prev;
        });
        setNewTagValue('');
        setAddTagField(null);
    }, []);

    const handleDropdownChange = useCallback((key: string, value: string) => {
        setFieldValues((prev) => ({ ...prev, [key]: value }));
    }, []);

    const handleCrosstabConfirm = useCallback(
        (crosstab: CrosstabItem, summary: CrosstabDataSummary) => {
            setSelectedCrosstab(crosstab);
            if (onCrosstabConfirm) {
                // onCrosstabConfirm owns the full flow: loads crosstab data then
                // sends a single message to DANI with data included. Do NOT also
                // call onFieldSubmit — that sends a bare message (no data) that
                // races ahead and triggers DANI before the data arrives.
                onCrosstabConfirm(crosstab, summary);
            } else {
                onFieldSubmit?.({ crosstab_id: crosstab.id, crosstab_name: crosstab.name });
            }
        },
        [onCrosstabConfirm, onFieldSubmit],
    );

    const handleToggleModule = useCallback((moduleId: string) => {
        setModules((prev) =>
            prev.map((m) => (m.id === moduleId && !m.locked ? { ...m, enabled: !m.enabled } : m)),
        );
    }, []);

    const handleSubmit = useCallback(
        (pathId: string, format: 'pdf' | 'pptx') => {
            const payload = {
                action: 'run_report',
                run_id: plan.run_id,
                brand: plan.brand_name,
                competitors: (fieldValues['competitors'] ?? []) as string[],
                verticals: (fieldValues['verticals'] ?? []) as string[],
                enabled_modules: modules.filter((m) => m.enabled).map((m) => m.id),
                time_period: (fieldValues['time_period'] as string) || 'last_90_days',
                depth: pathId,
                output_format: format,
            };
            onSubmit(JSON.stringify(payload), pathId, format);
        },
        [plan.run_id, plan.brand_name, fieldValues, modules, onSubmit],
    );

    const handleFieldContinue = useCallback(() => {
        onFieldSubmit?.(fieldValues);
    }, [fieldValues, onFieldSubmit]);

    const handleModuleContinue = useCallback(() => {
        const enabledIds = modules.filter((m) => m.enabled).map((m) => m.id);
        onModuleSubmit?.(enabledIds);
    }, [modules, onModuleSubmit]);

    const enabledModuleCount = modules.filter((m) => m.enabled).length;
    const isDisabled = isReadOnly || !!loadingPathId;
    const isOptionsDisabled = isDisabled || !!selectedOptionId;
    const isFieldsDisabled = isDisabled || isFieldSubmitted;
    const isModulesDisabled = isDisabled || isModuleSubmitted;
    const submitLabel = plan.stepMode?.isLast ? 'Submit' : 'Continue';

    const renderVerticalField = (field: PlannerField) => {
        if (!field) return null;
        const currentValues = (fieldValues[field.key] ?? field.values ?? []) as string[];
        const suggested = field.suggested ?? [];
        const available = suggested.filter((s) => !currentValues.includes(s));

        return (
            <div key={field.key}>
                <p className="mb-2 text-small font-medium text-default-600">{field.label}</p>
                <div className="flex flex-wrap items-center gap-1.5">
                    {currentValues.map((v) => (
                        <Chip
                            key={v}
                            variant="flat"
                            size="sm"
                            classNames={{
                                base: 'bg-mfour-green/10 text-mfour-green border border-mfour-green/20 transition-colors hover:bg-mfour-green/15',
                                closeButton: 'text-mfour-green/60 hover:text-mfour-green',
                            }}
                            onClose={!isDisabled ? () => handleRemoveTag(field.key, v) : undefined}
                        >
                            {v}
                        </Chip>
                    ))}
                    {!isDisabled && available.length > 0 && (
                        <Popover placement="bottom">
                            <PopoverTrigger>
                                <Button
                                    size="sm"
                                    variant="flat"
                                    className="h-6 min-w-0 gap-1 rounded-full bg-default-100 px-2.5 text-tiny font-medium text-default-500 data-[hover=true]:bg-default-200"
                                >
                                    <Icon icon="lucide:plus" width={12} />
                                    Add vertical
                                </Button>
                            </PopoverTrigger>
                            <PopoverContent className="p-1.5">
                                <div className="flex flex-col gap-0.5">
                                    {available.map((v) => (
                                        <Button
                                            key={v}
                                            size="sm"
                                            variant="light"
                                            className="justify-start"
                                            onPress={() => handleAddTag(field.key, v)}
                                        >
                                            {v}
                                        </Button>
                                    ))}
                                </div>
                            </PopoverContent>
                        </Popover>
                    )}
                </div>
            </div>
        );
    };

    const renderOptions = (options: PlanCardStepOption[]) => (
        <div className="flex max-w-md flex-col gap-3">
            {options.map((option) => {
                const isSelected = selectedOptionId === option.id;
                const interactive = !isOptionsDisabled || isSelected;

                return (
                    <div
                        key={option.id}
                        className={`relative flex min-w-0 cursor-pointer flex-col overflow-hidden rounded-xl border p-4 transition-all duration-150 ${
                            isSelected
                                ? 'border-mfour-green/50 bg-mfour-green/5 shadow-sm'
                                : isOptionsDisabled
                                  ? 'border-default-200 opacity-50'
                                  : 'border-default-200 hover:border-mfour-green/40 hover:bg-mfour-green/[0.02] hover:shadow-sm'
                        }`}
                        role={interactive ? 'button' : undefined}
                        tabIndex={interactive ? 0 : undefined}
                        onClick={() =>
                            !isOptionsDisabled && onOptionSelect?.(option.id, option.label)
                        }
                        onKeyDown={(e) => {
                            if (!isOptionsDisabled && (e.key === 'Enter' || e.key === ' ')) {
                                e.preventDefault();
                                onOptionSelect?.(option.id, option.label);
                            }
                        }}
                    >
                        {isSelected && (
                            <div className="absolute right-3 top-3 flex h-5 w-5 items-center justify-center rounded-full bg-mfour-green text-white">
                                <Icon icon="lucide:check" width={12} />
                            </div>
                        )}
                        <p className="break-words text-small font-semibold text-foreground">
                            {option.label}
                        </p>
                        <p className="mt-1.5 break-words text-tiny leading-relaxed text-default-400">
                            {option.description}
                        </p>
                    </div>
                );
            })}
        </div>
    );

    const renderStep = (step: PlannerStep, index: number) => {
        const isLast = index === plan.steps.length - 1;
        const hasOptions = step.options && step.options.length > 0;
        const hasFields = step.fields && step.fields.length > 0;
        const hasModules =
            (step.modules && step.modules.length > 0) ||
            (step.suggested_modules && step.suggested_modules.length > 0);
        const hasPaths = step.paths && step.paths.length > 0;

        console.log('[PlannerCard] renderStep:', {
            stepId: step.id,
            stepN: step.step,
            hasOptions,
            hasFields,
            hasModules,
            hasPaths,
            isStepMode,
            search_hint: step.search_hint,
        });

        if (step.id === 'crosstab_retrieval_by_org') {
            if (isFieldSubmitted) {
                return (
                    <div key={step.step} className="px-6 pb-6 pt-5">
                        <div className="flex items-center gap-3 rounded-xl border border-primary/20 bg-primary/5 px-4 py-3">
                            <div className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-lg bg-blue-600 text-white">
                                <Icon icon="lucide:check" width={16} />
                            </div>
                            <div className="min-w-0 flex-1">
                                <p className="truncate text-[13px] font-semibold text-foreground">
                                    {selectedCrosstab?.name ?? 'Crosstab selected'}
                                </p>
                                <p className="text-[11px] text-default-500">Crosstab confirmed</p>
                            </div>
                        </div>
                    </div>
                );
            }
            return (
                <div key={step.step} className="px-6 pb-6 pt-5">
                    <CrosstabPicker
                        isReadOnly={isDisabled || isFieldSubmitted}
                        isSubmitting={isFieldSubmitted}
                        onConfirm={handleCrosstabConfirm}
                        initialSelection={selectedCrosstab}
                        defaultSearch={step.search_hint}
                    />
                </div>
            );
        }

        if (isStepMode) {
            return (
                <div key={step.step} className="px-6 pb-6 pt-5">
                    {hasOptions && renderOptions(step.options!)}

                    {hasFields && (
                        <div className="space-y-4">
                            {step.fields!.map((field) => (
                                <DataInputField
                                    key={field.key}
                                    input={field}
                                    value={
                                        fieldValues[field.key] ?? field.values ?? field.value ?? ''
                                    }
                                    isReadOnly={isFieldsDisabled}
                                    addTagField={addTagField}
                                    newTagValue={newTagValue}
                                    onSetAddTagField={setAddTagField}
                                    onSetNewTagValue={setNewTagValue}
                                    onRemoveTag={handleRemoveTag}
                                    onAddTag={handleAddTag}
                                    onDropdownChange={handleDropdownChange}
                                />
                            ))}
                            <Button
                                className="mt-2 bg-mfour-green font-medium text-white data-[hover=true]:bg-mfour-green/90"
                                size="sm"
                                isDisabled={isFieldsDisabled}
                                onPress={handleFieldContinue}
                                endContent={<Icon icon="lucide:arrow-right" width={14} />}
                            >
                                {submitLabel}
                            </Button>
                        </div>
                    )}

                    {hasModules && (
                        <div className="space-y-4">
                            <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                                {modules.map((mod) => (
                                    <ModuleCard
                                        key={mod.id}
                                        module={mod}
                                        isReadOnly={isModulesDisabled}
                                        onToggle={handleToggleModule}
                                    />
                                ))}
                            </div>
                            <Button
                                className="mt-2 bg-mfour-green font-medium text-white data-[hover=true]:bg-mfour-green/90"
                                size="sm"
                                isDisabled={isModulesDisabled}
                                onPress={handleModuleContinue}
                                endContent={<Icon icon="lucide:arrow-right" width={14} />}
                            >
                                {submitLabel}
                            </Button>
                        </div>
                    )}

                    {hasPaths && (
                        <ReportSubmit
                            paths={step.paths!}
                            isReadOnly={isDisabled}
                            loadingPathId={loadingPathId}
                            onSubmit={handleSubmit}
                        />
                    )}
                </div>
            );
        }

        if (hasFields) {
            return (
                <SectionWrapper
                    key={step.step}
                    number={step.step}
                    title={step.label}
                    isLast={isLast}
                >
                    <div className="space-y-4">
                        {step.fields!.map((field) => {
                            if (field.type === 'multi_tag' && field.key === 'verticals') {
                                return renderVerticalField(field);
                            }
                            return (
                                <DataInputField
                                    key={field.key}
                                    input={field}
                                    value={
                                        fieldValues[field.key] ?? field.values ?? field.value ?? ''
                                    }
                                    isReadOnly={isDisabled}
                                    addTagField={addTagField}
                                    newTagValue={newTagValue}
                                    onSetAddTagField={setAddTagField}
                                    onSetNewTagValue={setNewTagValue}
                                    onRemoveTag={handleRemoveTag}
                                    onAddTag={handleAddTag}
                                    onDropdownChange={handleDropdownChange}
                                />
                            );
                        })}
                    </div>
                </SectionWrapper>
            );
        }

        if (hasModules) {
            return (
                <SectionWrapper
                    key={step.step}
                    number={step.step}
                    title={step.label}
                    isLast={isLast}
                >
                    {modules.length > 0 && (
                        <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                            {modules.map((mod) => (
                                <ModuleCard
                                    key={mod.id}
                                    module={mod}
                                    isReadOnly={isDisabled}
                                    onToggle={handleToggleModule}
                                />
                            ))}
                        </div>
                    )}
                </SectionWrapper>
            );
        }

        if (hasPaths) {
            return (
                <SectionWrapper
                    key={step.step}
                    number={step.step}
                    title={step.label}
                    isLast={isLast}
                >
                    <ReportSubmit
                        paths={step.paths!}
                        isReadOnly={isDisabled}
                        loadingPathId={loadingPathId}
                        onSubmit={handleSubmit}
                    />
                </SectionWrapper>
            );
        }

        return null;
    };

    return (
        <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.35, ease: 'easeOut' }}
            className="min-w-0 max-w-full"
        >
            <div className="overflow-hidden rounded-xl border border-default-200 bg-background shadow-sm">
                {isStepMode ? (
                    <div className="border-b border-default-100 px-6 pb-5 pt-6">
                        <div className="flex items-start justify-between gap-3">
                            <div className="min-w-0 flex-1">
                                <h3 className="text-tiny font-semibold uppercase tracking-widest text-default-400">
                                    {plan.steps[0]?.label}
                                </h3>
                                {plan.stepMode!.commentary && (
                                    <p className="mt-2 text-small leading-relaxed text-foreground/70">
                                        {plan.stepMode!.commentary}
                                    </p>
                                )}
                            </div>
                            {!isInlineWidget && (
                                <Chip
                                    size="sm"
                                    variant="flat"
                                    startContent={
                                        <Icon icon="lucide:layers" width={12} className="ml-1" />
                                    }
                                    classNames={{
                                        base: 'bg-default-100 h-6',
                                        content: 'text-tiny font-medium text-default-600 px-1',
                                    }}
                                >
                                    Step {plan.stepMode!.currentStep} of {plan.stepMode!.totalSteps}
                                </Chip>
                            )}
                        </div>
                    </div>
                ) : (
                    <div className="border-b border-default-100 px-6 pb-5 pt-6">
                        <div className="flex items-start justify-between gap-3">
                            <div className="min-w-0 flex-1">
                                <h2 className="text-base font-semibold leading-snug text-foreground">
                                    {plan.title}
                                </h2>
                            </div>
                            <div className="flex flex-shrink-0 flex-wrap items-center gap-2">
                                <Chip
                                    size="sm"
                                    variant="flat"
                                    startContent={
                                        <Icon icon="lucide:layers" width={12} className="ml-1" />
                                    }
                                    classNames={{
                                        base: 'bg-default-100 h-6',
                                        content: 'text-tiny font-medium text-default-600 px-1',
                                    }}
                                >
                                    {plan.steps.length} steps
                                </Chip>
                                {enabledModuleCount > 0 && (
                                    <Chip
                                        size="sm"
                                        variant="flat"
                                        startContent={
                                            <Icon
                                                icon="lucide:puzzle"
                                                width={12}
                                                className="ml-1"
                                            />
                                        }
                                        classNames={{
                                            base: 'bg-mfour-green/10 h-6',
                                            content: 'text-tiny font-medium text-mfour-green px-1',
                                        }}
                                    >
                                        {enabledModuleCount} modules
                                    </Chip>
                                )}
                            </div>
                        </div>
                        {plan.assumption_note && (
                            <div className="mt-3 flex items-start gap-2 rounded-lg bg-warning/10 px-3 py-2">
                                <Icon
                                    icon="lucide:info"
                                    width={14}
                                    className="mt-0.5 flex-shrink-0 text-warning"
                                />
                                <p className="text-tiny leading-relaxed text-warning-600">
                                    {plan.assumption_note}
                                </p>
                            </div>
                        )}
                    </div>
                )}

                {isStepMode ? (
                    plan.steps.map((step, index) => renderStep(step, index))
                ) : (
                    <div className="px-6 pb-2 pt-6">
                        {plan.steps.map((step, index) => renderStep(step, index))}
                    </div>
                )}
            </div>
        </motion.div>
    );
}
